/**
 * Pure rules behind the Process Control screens: what the running release says about a tag, how a value and its
 * quality are shown, and how history becomes line segments WITHOUT bridging bad or missing data (URS-CTL-018:
 * a gap is drawn as a gap, never interpolated across).
 */
import type { AlarmRow, HistoryPoint, Quality, ReleaseContentView } from "./control-api.js";

export const MODE_NAMES: Record<number, string> = { 0: "MAN", 1: "AUTO", 2: "CAS" };
/** A stable tag still writes one sample per heartbeat; silence for longer than this many heartbeats is a gap. */
export const GAP_HEARTBEATS = 2.5;
const DEFAULT_HEARTBEAT_S = 60;

export interface AlarmInfo {
  id: string;
  unit: string;
  input: string;
  type: string;
  limit: number | null;
  priority: number;
  consequence: string | null;
  response: string | null;
}
export interface BlockInfo {
  id: string;
  unit: string;
  module: string;
  kind: string;
  cfg: Record<string, unknown>;
}
export interface Catalog {
  blocks: Map<string, BlockInfo>;
  historian: Map<string, { deadband: number; eu: string | null; heartbeatS: number; unit: string }>;
  alarms: AlarmInfo[];
  signed: Set<string>;
  units: string[];
}

export function buildCatalog(content: ReleaseContentView): Catalog {
  const cat: Catalog = {
    blocks: new Map(),
    historian: new Map(),
    alarms: [],
    signed: new Set(),
    units: content.units.map((u) => u.id),
  };
  for (const u of content.units) {
    for (const m of u.modules) {
      for (const b of m.blocks) {
        const id = String(b["id"]);
        cat.blocks.set(id, { id, unit: u.id, module: m.id, kind: String(b["kind"]), cfg: b });
      }
      for (const a of m.alarms ?? [])
        cat.alarms.push({
          id: String(a["id"]),
          unit: u.id,
          input: String(a["input"]),
          type: String(a["type"]),
          limit: typeof a["limit"] === "number" ? a["limit"] : null,
          priority: Number(a["priority"]),
          consequence: (a["consequence"] as string | undefined) ?? null,
          response: (a["response"] as string | undefined) ?? null,
        });
    }
    for (const h of u.historian)
      cat.historian.set(h.tag, {
        deadband: h.deadband,
        eu: h.eu ?? null,
        heartbeatS: h.heartbeatS ?? DEFAULT_HEARTBEAT_S,
        unit: u.id,
      });
    for (const t of u.signedWrites) cat.signed.add(t);
  }
  return cat;
}

export interface TagMeta {
  tag: string;
  block: string;
  output: string;
  unit: string | null;
  module: string | null;
  eu: string | null;
  /** Engineering range or configured write limits for the tag; null when the configuration states none. */
  range: [number, number] | null;
  writable: boolean;
  signed: boolean;
  historized: boolean;
  deadband: number | null;
}

export const splitTag = (tag: string): { block: string; output: string } => {
  const i = tag.lastIndexOf(".");
  return { block: tag.slice(0, i), output: tag.slice(i + 1) };
};

const num = (x: unknown): number | null => (typeof x === "number" ? x : null);
const pair = (a: unknown, b: unknown): [number, number] | null =>
  num(a) !== null && num(b) !== null ? [a as number, b as number] : null;

function rangeOf(cat: Catalog, block: string, output: string): [number, number] | null {
  const b = cat.blocks.get(block);
  if (!b) return null;
  if (b.kind === "AI") return pair(b.cfg["euLo"], b.cfg["euHi"]);
  if (b.kind !== "PID") return null;
  if (output === "SP") return pair(b.cfg["spLo"], b.cfg["spHi"]);
  if (output === "OUT") return pair(b.cfg["outLo"], b.cfg["outHi"]);
  if (output === "PV") {
    const src = typeof b.cfg["pv"] === "string" ? splitTag(b.cfg["pv"]) : null;
    return src ? rangeOf(cat, src.block, src.output) : null;
  }
  return null;
}

/**
 * A PID's PV and SP are in the measurement's unit, and only OUT is a percent (#37). A release built before that was
 * fixed stored the controller's "%" for all three, so the unit is read from the measurement's own historian entry
 * first; the stored one is used when the measurement is not historized.
 */
function euOf(cat: Catalog, b: BlockInfo | undefined, output: string, stored: string | null): string | null {
  if (b?.kind !== "PID") return stored;
  if (output === "OUT") return stored ?? "%";
  if (output !== "PV" && output !== "SP") return stored;
  const src = typeof b.cfg["pv"] === "string" ? cat.historian.get(b.cfg["pv"])?.eu : undefined;
  return src ?? stored;
}

export function tagMeta(cat: Catalog, tag: string): TagMeta {
  const { block, output } = splitTag(tag);
  const b = cat.blocks.get(block);
  const h = cat.historian.get(tag);
  const writable =
    b?.kind === "PID"
      ? ["SP", "OUT", "MODE"].includes(output)
      : b?.kind === "INTERLOCK" && output === "RESET";
  return {
    tag,
    block,
    output,
    unit: b?.unit ?? null,
    module: b?.module ?? null,
    eu: euOf(cat, b, output, h?.eu ?? null),
    range: rangeOf(cat, block, output),
    writable,
    signed: cat.signed.has(tag),
    historized: h !== undefined,
    deadband: h?.deadband ?? null,
  };
}

/** The wording of a process value: numbers trimmed, booleans as words, MODE as its name. Never blank. */
export function formatValue(tag: string, v: number | boolean): string {
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  if (tag.endsWith(".MODE")) return MODE_NAMES[v] ?? String(v);
  return String(Number(v.toFixed(3)));
}

/** "2026-11-01 10:00:00 UTC": every timestamp on these screens carries its zone. */
export const utc = (iso: string | null | undefined): string =>
  iso ? `${new Date(iso).toISOString().slice(0, 19).replace("T", " ")} UTC` : "no value yet";

export function clockLabel(c: { mode: string; factor: number }): string {
  return c.mode === "REALTIME" ? "Real time" : `Accelerated x${c.factor}`;
}

export const worstQuality = (qs: Quality[]): Quality =>
  qs.includes("BAD") ? "BAD" : qs.includes("UNCERTAIN") ? "UNCERTAIN" : "GOOD";

// -------------------------------------------------------------------- alarms

export const ACTIVE_STATES = new Set(["UNACK_ACTIVE", "ACK_ACTIVE", "UNACK_RTN"]);
export const STATE_LABEL: Record<string, string> = {
  UNACK_ACTIVE: "Active, unacknowledged",
  ACK_ACTIVE: "Active, acknowledged",
  UNACK_RTN: "Returned, unacknowledged",
  SHELVED: "Shelved",
  SUPPRESSED: "Suppressed",
  OUT_OF_SERVICE: "Out of service",
};

/** Active unacknowledged alarms and the highest (lowest-numbered) priority among them, as the shell shows it. */
export function unackSummary(alarms: AlarmRow[]): { count: number; highest: number | null } {
  const un = alarms.filter((a) => a.awaitingAck);
  return { count: un.length, highest: un.length ? Math.min(...un.map((a) => a.priority)) : null };
}

export function alarmsByPriority(alarms: AlarmRow[]): Record<number, number> {
  const out: Record<number, number> = {};
  for (const a of alarms) if (ACTIVE_STATES.has(a.state)) out[a.priority] = (out[a.priority] ?? 0) + 1;
  return out;
}

export function activeSince(changedAt: string | null, now: number): string {
  if (!changedAt) return "unknown";
  const s = Math.max(0, Math.floor((now - Date.parse(changedAt)) / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min ${s % 60} s`;
  return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
}

// -------------------------------------------------------------------- trends

export interface Segment {
  quality: "GOOD" | "UNCERTAIN";
  pts: { t: number; v: number }[];
}

/**
 * Deadband-compressed history is a step function: a value holds until the next stored sample or until
 * `maxHoldMs` passes without one. A BAD sample, or silence longer than `maxHoldMs`, ENDS the line; nothing is drawn
 * across it. UNCERTAIN stretches are separate segments so they can be drawn differently from good data.
 */
export function toSegments(points: HistoryPoint[], maxHoldMs: number): Segment[] {
  const out: Segment[] = [];
  let cur: Segment | null = null;
  points.forEach((p, i) => {
    const t = Date.parse(p.t);
    if (p.s === "BAD") {
      cur = null;
      return;
    }
    const next = points[i + 1];
    const nextT = next ? Date.parse(next.t) : null;
    const contiguous = nextT !== null && nextT - t <= maxHoldMs && next?.s !== "BAD";
    const end = contiguous ? (nextT as number) : t + (nextT === null ? 0 : Math.min(maxHoldMs, nextT - t));
    if (!cur || cur.quality !== p.s) {
      cur = { quality: p.s, pts: [] };
      out.push(cur);
    }
    cur.pts.push({ t, v: p.v }, { t: end, v: p.v });
    if (!contiguous) cur = null;
  });
  return out;
}

export function maxHoldMs(cat: Catalog | null, tag: string): number {
  const hb = cat?.historian.get(tag)?.heartbeatS ?? DEFAULT_HEARTBEAT_S;
  return hb * GAP_HEARTBEATS * 1000;
}

/** The value a pen held at time `t`, or null when `t` falls in a gap (bad or missing data), never an estimate. */
export function valueAt(segs: Segment[], t: number): { v: number; quality: Segment["quality"] } | null {
  for (const s of segs) {
    const first = s.pts[0];
    const last = s.pts[s.pts.length - 1];
    if (!first || !last || t < first.t || t > last.t) continue;
    let v = first.v;
    for (const p of s.pts) if (p.t <= t) v = p.v;
    return { v, quality: s.quality };
  }
  return null;
}

/**
 * A pen's own scale: the span of its data, padded; a flat line still gets a visible span. With no data at all the scale
 * is the tag's configured range (an engineering scale, not an invented 0 to 1), and only without that a unit span.
 */
export function scaleOf(
  segs: Segment[],
  limits: number[] = [],
  range: [number, number] | null = null,
): [number, number] {
  const vs = [...segs.flatMap((s) => s.pts.map((p) => p.v)), ...limits];
  if (vs.length === 0) return range ? [range[0], range[1]] : [0, 1];
  const lo = Math.min(...vs);
  const hi = Math.max(...vs);
  const pad = hi === lo ? Math.abs(lo || 1) * 0.1 : (hi - lo) * 0.08;
  return [lo - pad, hi + pad];
}

export function csvOf(series: Record<string, HistoryPoint[]>, eu: (tag: string) => string | null): string {
  // a spreadsheet runs a text cell that starts with = + - @ TAB or CR: such a cell gets a leading apostrophe
  const esc = (s0: string) => {
    const s = /^[=+\-@\t\r]/.test(s0) ? `'${s0}` : s0;
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = ["tag,time_utc,value,unit,quality,stored_because"];
  for (const [tag, pts] of Object.entries(series))
    for (const p of pts) lines.push([esc(tag), p.t, String(p.v), esc(eu(tag) ?? ""), p.s, p.k].join(","));
  return `${lines.join("\n")}\n`;
}
